/**
 * Achievement DEFINITION builder — the pure, validated constructor the operator
 * badge editor (`apps/dashboard/src/routes/loyalty-achievements.tsx`) runs before
 * it writes a def to `tenants/{tid}/loyaltyProgram/config/achievements/{id}`.
 *
 * Two hard constraints this helper enforces so operator authoring can never make
 * a badge money-adjacent or un-earnable:
 *
 *   1. **Reward is ALWAYS `{ kind: 'none' }`.** Badges are the dopamine — nothing
 *      on the money path is granted on unlock. Points/voucher rewards are OUT OF
 *      SCOPE for operator-authored badges (they'd make the surface money-adjacent
 *      + owner-reviewed). The seeded defaults are all `reward:none` too.
 *   2. **Only the AUTO-EVALUABLE criteria kinds may be authored.** `onTicketCollected`
 *      can compute `first_wash`/`n_visits`/`spent_over`/`streak_n_weeks` from the
 *      stats the loyalty pipeline already tracks (see `achievements-eval.ts`
 *      `isAutoEvaluableCriteria`). The per-item kinds (`package_count`/`add_on_count`)
 *      + hand-awarded `custom` never auto-earn, so surfacing them would author a
 *      dead-end badge that can never unlock.
 *
 * Pure + environment-agnostic (packages/shared rules): builds + validates via
 * `AchievementSchema`, throws on invalid, touches no I/O. The final write goes
 * through the rules-guarded `achievementsRepoFor` repo (which re-validates).
 */

import { AchievementSchema, type Achievement } from '../schemas/loyalty.js';
import type { I18nString } from '../schemas/primitives.js';

/**
 * Criteria kinds an operator may author in the badge editor — the subset of
 * `AchievementCriteriaSchema` that auto-evaluates. Kept in sync with
 * `isAutoEvaluableCriteria` (a test pins the two together).
 */
export const AUTHORABLE_ACHIEVEMENT_CRITERIA = [
  'first_wash',
  'n_visits',
  'spent_over',
  'streak_n_weeks',
] as const;
export type AuthorableAchievementCriteriaKind = (typeof AUTHORABLE_ACHIEVEMENT_CRITERIA)[number];

/**
 * Whether a criteria kind carries an editable numeric threshold. `first_wash`
 * is the only thresholdless kind (it fires on visit #1); the rest need a target
 * (washes, integer sen, or weeks).
 */
export function achievementCriteriaNeedsThreshold(
  kind: AuthorableAchievementCriteriaKind,
): boolean {
  return kind !== 'first_wash';
}

/** Why a badge definition couldn't be built — machine code + human message. */
export type AchievementDefinitionErrorCode = 'invalid_threshold' | 'invalid_name' | 'invalid_shape';

export class InvalidAchievementDefinitionError extends Error {
  readonly code: AchievementDefinitionErrorCode;
  constructor(code: AchievementDefinitionErrorCode, message: string) {
    super(message);
    this.name = 'InvalidAchievementDefinitionError';
    this.code = code;
  }
}

export interface AchievementDefinitionInput {
  /** Short id; equals the Firestore doc id. Must satisfy `ShortIdSchema`. */
  readonly id: string;
  /** Emoji/glyph shown on the badge. */
  readonly icon: string;
  /** Per-locale badge name (en required). */
  readonly name: I18nString;
  /** Per-locale one-line description (en required). */
  readonly description: I18nString;
  /** Which auto-evaluable milestone this badge tracks. */
  readonly criteriaKind: AuthorableAchievementCriteriaKind;
  /**
   * Target for the thresholded kinds:
   *   - `n_visits`       → number of paid washes (whole, ≥ 1)
   *   - `streak_n_weeks` → consecutive weeks (whole, ≥ 1)
   *   - `spent_over`     → INTEGER SEN (≥ 1) — the caller converts RM → sen
   * Ignored for `first_wash`.
   */
  readonly threshold?: number;
  /** Operator kill-switch; an inactive badge never auto-earns or shows. */
  readonly active: boolean;
}

/** Build the discriminated criteria union from an authorable kind + threshold. */
function criteriaFrom(
  kind: AuthorableAchievementCriteriaKind,
  threshold: number | undefined,
): Achievement['criteria'] {
  if (kind === 'first_wash') return { kind: 'first_wash' };

  const raw = typeof threshold === 'number' ? threshold : Number.NaN;
  if (!Number.isFinite(raw) || raw < 1) {
    throw new InvalidAchievementDefinitionError(
      'invalid_threshold',
      'Threshold must be a whole number of 1 or more.',
    );
  }
  const n = Math.round(raw);
  switch (kind) {
    case 'n_visits':
      return { kind: 'n_visits', n };
    case 'streak_n_weeks':
      return { kind: 'streak_n_weeks', n };
    case 'spent_over':
      // `threshold` is already integer sen here (caller converts RM → sen).
      return { kind: 'spent_over', amount: n };
  }
}

/**
 * Construct a validated, reward-free achievement definition from operator input.
 *
 * Throws `InvalidAchievementDefinitionError` on a bad threshold or an empty
 * English name, and rethrows a Zod error (wrapped) if the assembled shape fails
 * `AchievementSchema` (e.g. an id that violates `ShortIdSchema`). On success the
 * returned object is safe to hand to `achievementsRepoFor(tid).set(id, def)`.
 */
export function buildAchievementDefinition(input: AchievementDefinitionInput): Achievement {
  if (!input.name.en || input.name.en.trim().length === 0) {
    throw new InvalidAchievementDefinitionError('invalid_name', 'Give the badge a name.');
  }

  const candidate = {
    id: input.id,
    icon: input.icon,
    name: input.name,
    description: input.description,
    criteria: criteriaFrom(input.criteriaKind, input.threshold),
    // Badges are the reward — never points/voucher (out of scope; money-adjacent).
    reward: { kind: 'none' as const },
    active: input.active,
  };

  const parsed = AchievementSchema.safeParse(candidate);
  if (!parsed.success) {
    throw new InvalidAchievementDefinitionError(
      'invalid_shape',
      parsed.error.issues[0]?.message ?? 'That badge is not valid.',
    );
  }
  return parsed.data;
}
